import { useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import * as Slider from '@radix-ui/react-slider'
import { RotateCcw, X, ChevronDown, ChevronUp } from 'lucide-react'
import { useRoadStore, filterRoads, type BikeType } from '@/lib/roads/store'
import { getMapTheme, subscribeMapTheme } from '../../lib/roads/theme'
import { useSyncExternalStore } from 'react'

interface FiltersPanelProps {
  open: boolean
  onClose: () => void
}

const BIKE_META: { type: BikeType; color: string; label: string }[] = [
  { type: 'Sport', color: '#FF4438', label: 'Sport' },
  { type: 'Cruiser', color: '#3B82F6', label: 'Cruiser' },
  { type: 'ADV', color: '#34D26B', label: 'ADV' },
  { type: 'Sumo', color: '#FF9F1C', label: 'Sumo' },
]

const RATING_FIELDS = [
  { key: 'minFun', label: 'Fun' },
  { key: 'minPavement', label: 'Pavement' },
  { key: 'minTraffic', label: 'Traffic' },
  { key: 'minDirt', label: 'Dirt · Debris' },
] as const

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">{children}</div>
  )
}

function RatingSlider({
  label,
  value,
  onChange,
}: {
  label: string
  value: number
  onChange: (v: number) => void
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-20 shrink-0 text-xs text-white/60">{label}</span>
      <Slider.Root
        className="relative flex h-11 flex-1 touch-none select-none items-center"
        value={[value]}
        min={0}
        max={10}
        step={0.5}
        onValueChange={(v) => onChange(v[0])}
      >
        <Slider.Track className="relative h-1 w-full grow rounded-full bg-white/10">
          <Slider.Range className="absolute h-full rounded-full bg-amber-400" />
        </Slider.Track>
        <Slider.Thumb className="block h-4 w-4 rounded-full bg-amber-400 outline-none ring-2 ring-black/40 focus-visible:ring-amber-200 cursor-grab" />
      </Slider.Root>
      <span className="w-9 text-right text-xs font-mono text-white/70">
        {value > 0 ? value.toFixed(1) : '—'}
      </span>
    </div>
  )
}

export default function FiltersPanel({ open, onClose }: FiltersPanelProps) {
  const filters = useRoadStore((s) => s.filters)
  const setFilters = useRoadStore((s) => s.setFilters)
  const resetFilters = useRoadStore((s) => s.resetFilters)
  const roads = useRoadStore((s) => s.roads)
  const meta = useRoadStore((s) => s.meta)

  const [moreRatings, setMoreRatings] = useState(false)
  const dark = useSyncExternalStore(subscribeMapTheme, getMapTheme) === 'dark'
  const [sheetH, setSheetH] = useState<number | null>(null)
  const asideRef = useRef<HTMLElement | null>(null)
  const dragRef = useRef<{ startY: number; startH: number } | null>(null)
  const dragRafRef = useRef<number | null>(null)
  const pendingHRef = useRef<number | null>(null)
  // During the drag the height is written straight to the DOM — zero React
  // re-renders (the map keeps its headroom); state catches up once at the end.
  const applyDragHeight = (h: number) => {
    const el = asideRef.current
    if (el) {
      el.style.height = `${h}px`
      el.style.maxHeight = `${h}px`
    }
  }

  // Pointer-based drag (touch + mouse) matching the road detail panel: the
  // grab bar + header block is the drag-sensitive zone, pointer capture keeps
  // the gesture alive when the finger/cursor leaves it.
  const onDragStart = (e: React.PointerEvent) => {
    if (window.matchMedia('(min-width: 768px)').matches) return
    if (e.pointerType === 'mouse' && e.button !== 0) return
    const current =
      sheetH ??
      asideRef.current?.offsetHeight ??
      Math.round(window.innerHeight * 0.4)
    dragRef.current = { startY: e.clientY, startH: current }
    try {
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    } catch {
      /* no capture available */
    }
  }
  const onDragMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return
    const dy = dragRef.current.startY - e.clientY
    const h = Math.min(
      Math.max(dragRef.current.startH + dy, 140),
      Math.round(window.innerHeight * 0.85),
    )
    pendingHRef.current = h
    if (dragRafRef.current == null) {
      dragRafRef.current = requestAnimationFrame(() => {
        dragRafRef.current = null
        if (pendingHRef.current != null) applyDragHeight(pendingHRef.current)
      })
    }
  }
  const onDragEnd = () => {
    if (dragRafRef.current != null) {
      cancelAnimationFrame(dragRafRef.current)
      dragRafRef.current = null
    }
    const h = pendingHRef.current
    pendingHRef.current = null
    dragRef.current = null
    if (h != null) {
      applyDragHeight(h)
      setSheetH(h)
    }
  }

  const regions = useMemo(() => {
    const m = meta as unknown as { regions?: string[] } | null
    return m?.regions ?? []
  }, [meta])

  const matchCount = useMemo(
    () => (open && roads.length ? filterRoads(roads, filters).length : 0),
    [open, roads, filters],
  )

  const toggleBike = (type: BikeType) => {
    const has = filters.bikeTypes.includes(type)
    setFilters({
      bikeTypes: has ? filters.bikeTypes.filter((b) => b !== type) : [...filters.bikeTypes, type],
    })
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          key="filters-panel"
          ref={asideRef}
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ type: 'spring', damping: 26, stiffness: 260 }}
            style={sheetH != null ? { height: sheetH, maxHeight: sheetH } : undefined}
            className={`fixed md:absolute z-50 border-2 border-white rounded-xl flex flex-col md:backdrop-blur
              ${dark ? 'bg-[#0C0C0E]/95' : 'bg-[#3A3A3C]/95'}
              inset-x-0 bottom-0 max-h-[75vh] overflow-hidden rounded-b-none
              md:inset-auto md:left-3 md:top-[76px] md:bottom-auto md:w-80 md:max-h-[calc(100dvh-100px)]`}
          >
            {/* Grab bar + header = drag-sensitive zone (mobile only) */}
            <div
              onPointerDown={onDragStart}
              onPointerMove={onDragMove}
              onPointerUp={onDragEnd}
              onPointerCancel={onDragEnd}
              className="shrink-0 z-10 select-none touch-none rounded-t-xl p-4 pb-0"
            >
              <div className="flex justify-center pt-2.5 pb-2 md:hidden">
                <div className="w-10 h-1 rounded-full bg-white/25" />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <SectionLabel>Filters</SectionLabel>
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300">
                    {matchCount} match
                  </span>
                </div>
                <div className="flex items-center">
                  <button
                    onClick={resetFilters}
                    className="min-h-11 px-3 rounded-lg text-xs uppercase font-mono tracking-wider text-white/50 hover:text-white flex items-center gap-1.5"
                  >
                    <RotateCcw size={14} />
                    Reset filters
                  </button>
                  <button
                    onClick={onClose}
                    className="h-11 w-11 -mr-2 flex items-center justify-center rounded-lg text-white/50 hover:text-white"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            </div>

            <div className="overflow-y-auto min-h-0 flex-1 overscroll-contain p-4 pt-3 space-y-4 rounded-b-xl">

            {/* GOOD FOR */}
            <div className="space-y-2">
              <SectionLabel>Good for</SectionLabel>
              <div className="flex flex-wrap gap-1.5">
                {BIKE_META.map((b) => {
                  const active = filters.bikeTypes.includes(b.type)
                  return (
                    <button
                      key={b.type}
                      onClick={() => toggleBike(b.type)}
                      className={`min-h-11 px-3 rounded-lg border text-xs flex items-center gap-2 transition-colors ${
                        active
                          ? 'bg-amber-400/15 border-amber-400/60 text-amber-200'
                          : 'bg-white/[0.03] border-white/10 text-white/60 hover:border-white/25'
                      }`}
                    >
                      <span className="flex flex-col items-stretch gap-0.5">
                        {b.label}
                        <span className="h-[2px] rounded-full" style={{ background: b.color }} />
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* MIN OVERALL */}
            <div className="space-y-2">
              <SectionLabel>Minimum overall rating</SectionLabel>
              <RatingSlider
                label="Overall"
                value={filters.minOverall}
                onChange={(v) => setFilters({ minOverall: v })}
              />
            </div>

            {/* More rating filters */}
            <div className="space-y-2">
              <button
                onClick={() => setMoreRatings((v) => !v)}
                className="min-h-11 w-full flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-white/40 hover:text-white/70"
              >
                More rating filters
                {moreRatings ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {moreRatings && (
                <div className="space-y-1.5">
                  {RATING_FIELDS.map((f) => (
                    <RatingSlider
                      key={f.key}
                      label={f.label}
                      value={filters[f.key]}
                      onChange={(v) => setFilters({ [f.key]: v })}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* LENGTH */}
            <div className="space-y-2">
              <SectionLabel>Road length (miles)</SectionLabel>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  value={filters.minLength || ''}
                  placeholder="Min"
                  onChange={(e) =>
                    setFilters({ minLength: e.target.value ? Number(e.target.value) : 0 })
                  }
                  className="h-11 w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-amber-400/60"
                />
                <span className="text-white/30 text-xs">–</span>
                <input
                  type="number"
                  min={0}
                  value={filters.maxLength ?? ''}
                  placeholder="Any"
                  onChange={(e) =>
                    setFilters({ maxLength: e.target.value ? Number(e.target.value) : 0 })
                  }
                  className="h-11 w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-amber-400/60"
                />
              </div>
            </div>

            {/* TOGGLES */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-3 min-h-11 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.fullyPavedOnly}
                  onChange={(e) => setFilters({ fullyPavedOnly: e.target.checked })}
                  className="h-4 w-4 accent-amber-400"
                />
                <span className="text-sm text-white/75">Fully paved only</span>
              </label>
              <label className="flex items-center gap-3 min-h-11 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.highwaysOnly}
                  onChange={(e) => setFilters({ highwaysOnly: e.target.checked })}
                  className="h-4 w-4 accent-amber-400"
                />
                <span className="text-sm text-white/75">Highways only</span>
              </label>
            </div>

            {/* RATED / UNRATED VISIBILITY */}
            <div className="space-y-1.5">
              <SectionLabel>Show roads</SectionLabel>
              <label className="flex items-center gap-3 min-h-11 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.showRated}
                  onChange={(e) => setFilters({ showRated: e.target.checked })}
                  className="h-4 w-4 accent-amber-400"
                />
                <span className="text-sm text-white/75">Rated</span>
              </label>
              <label className="flex items-center gap-3 min-h-11 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.showUnrated}
                  onChange={(e) => setFilters({ showUnrated: e.target.checked })}
                  className="h-4 w-4 accent-amber-400"
                />
                <span className="text-sm text-white/75">Unrated</span>
              </label>
            </div>

            {/* REGION */}
            {regions.length > 0 && (
              <div className="space-y-2">
                <SectionLabel>Region</SectionLabel>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    onClick={() => setFilters({ region: null })}
                    className={`min-h-11 px-3 rounded-lg border text-xs transition-colors ${
                      filters.region === null
                        ? 'bg-amber-400/15 border-amber-400/60 text-amber-200'
                        : 'bg-white/[0.03] border-white/10 text-white/60'
                    }`}
                  >
                    All
                  </button>
                  {regions.map((r) => (
                    <button
                      key={r}
                      onClick={() => setFilters({ region: filters.region === r ? null : r })}
                      className={`min-h-11 px-3 rounded-lg border text-xs transition-colors ${
                        filters.region === r
                          ? 'bg-amber-400/15 border-amber-400/60 text-amber-200'
                          : 'bg-white/[0.03] border-white/10 text-white/60 hover:border-white/25'
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* FOOTER */}
            <div className="pt-2 border-t border-white/10 flex items-center justify-end">
              <span className="text-xs font-mono text-amber-300">{matchCount} roads match</span>
            </div>
            </div>
          </motion.aside>
      )}
    </AnimatePresence>
  )
}
